<template>
  <CardSection sectionCaption="Pending Reports">
    <template #content>
      <PvDataTable :value="pendingReports"
                   :rows="10"
                   :size="'small'"
                   :paginator="pendingReports && pendingReports.length > 10" >
        <PvColumn header="Actions" style="width: 110px;">
          <template #body="slotProps">
            <PvButton v-if="slotProps.data.reportId"
                      label="View" 
                      @click="viewReport(slotProps.data)" 
                      size="small"
                      severity="primary" 
                      rounded outlined
                      :icon="ICONS.View" 
                      :title="`View annual report for ${slotProps.data.buName} - ${slotProps.data.periodDesc}.`"
                      :aria-label="`View annual report for ${slotProps.data.buName} - ${slotProps.data.periodDesc}.`"
                      class="button-w-100" />      
            <PvButton v-else 
                      label="Create"
                      @click="viewReport(slotProps.data)" 
                      severity="primary" 
                      rounded outlined
                      :icon="ICONS.Add"
                      size="small" 
                      :title="`Create annual report for ${slotProps.data.buName} - ${slotProps.data.periodDesc}.`"
                      :aria-label="`Create annual report for ${slotProps.data.buName} - ${slotProps.data.periodDesc}.`"
                      class="button-w-100" />
          </template>
        </PvColumn>
        <PvColumn key="reportId" field="reportType" header="Report Type" />
        <PvColumn field="periodDesc" header="Report Year" />
        <PvColumn field="hccisId" header="ID" />
        <PvColumn field="buName" header="Organization" style="width: 30%" />
        <PvColumn field="reportStatus" header="Report Status" />
        <PvColumn field="dueDate" header="Due Date" class="date-column">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.dueDate) }}
          </template>
        </PvColumn>          
        <template #empty>
          <NoData message="No outstanding reports." />
        </template>
      </PvDataTable>
      <div class="text-center mt-3">
        <PvButton v-if="pendingReports && pendingReports.length > 0"
                  label="Go To Annual Reports"
                  type="button"
                  :icon="ICONS.View"
                  rounded
                  @click="router.push('/annualreports')" />
      </div>         
    </template>
  </CardSection>
  <PvDialog v-model:visible="dialogVisible" modal :closable="false" aria-labelledby="report-access-dialog" style="width:75%">
    <template #header>
      <h2 id="report-access-dialog" class="mx-2">Report Access</h2>
    </template>
    <template #default>
      <p>Only report contacts may access this report. If you would like to add yourself as a report contact, please select a role and select 'View Report'</p>
      <PvDataTable v-model:selection="selectedRole" :value="contactRoles" size="small" style="width: 100%">
        <PvColumn selectionMode="single" title="Select One" header="Select One" />
        <PvColumn field="textValue1" header="Role" style="width:250px" />
        <PvColumn field="textValue2" header="Description" />
      </PvDataTable>
    </template>
    <template #footer>
      <PvButton label="View Report" @click="addUserAndViewReport()" class="action-btn me-2" rounded raised />
      <PvButton label="No" @click="dialogVisible = false" class="action-btn" severity="secondary" rounded raised />
    </template>
  </PvDialog>
</template>
<script setup>
// ===============================
// IMPORTS
// ===============================
// Vue Core
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

// Components
import CardSection from '../common/CardSection.vue';
import NoData from '../common/NoData.vue';

// Composables
import { usePageHelper } from '@/composables/pageHelper';
import { addReportContact, createAnnualReport } from '@/modules/hp/services/hpLambda';
import { addReportContact as addFoscReportContact, createFoscReport } from '@/modules/fosc/services/FoscReportApiService';
import { addReportContact as addCexReportContact, createCexReport } from '@/modules/cex/services/CexReportApiService';
import { addReportContact as addDiReportContact, createDIReport } from '@/modules/di/services/DIReportApiService';
import { addReportContact as addUroReportContact, createUroReport } from '@/modules/uro/services/UroReportApiService';

// Stores
import { useAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { useAlertStore } from '@/stores/alertStore';

// Constants and Utils
import { ICONS, REPORT_TYPES } from '@/utils/constants';
import { formatDate } from '@/utils/dataUtils';

// ===============================
// PROPS & EMITS
// ===============================
defineProps({
  pendingReports: {
    type: Array,
    required: false,
    default: () => []
  }
});

// ===============================
// COMPOSABLES & STORES
// ===============================
const pageHelper = usePageHelper();
const router = useRouter();
const appStore = useAppStore();
const authStore = useAuthStore();
const alert = useAlertStore();

// ===============================
// REACTIVE STATE
// ===============================
const selectedReport = ref(null);
const selectedRole = ref(null);
const dialogVisible = ref(false);

// ===============================
// CONSTANTS
// ===============================
const contactId = authStore.user.contactId;

// ===============================
// COMPUTED PROPERTIES
// ===============================
const contactRoles = computed(() => {
  if (!selectedReport.value) {
    return [];
  }

  const roleHandlers = {
    [REPORT_TYPES.Hp]: () => appStore.hpContactRoles(),
    [REPORT_TYPES.Fosc]: () => appStore.foscContactRoles(),
    [REPORT_TYPES.Di]: () => appStore.diContactRoles(),
    [REPORT_TYPES.Cex]: () => appStore.cexContactRoles(),
    [REPORT_TYPES.Uro]: () => appStore.uroContactRoles(),
  };

  const handler = roleHandlers[selectedReport.value.reportType];
  
  return handler ? handler() : [];
});

// ===============================
// METHODS
// ===============================
/**
 * Views the details of the selected annual report.
 * If the user is not a report contact, a dialog will be displayed to allow the user to add themselves to the report.
 * If the user is a report contact, the user will be redirected to the annual report page.
 * 
 * @param {Object} slotData - The data related to the selected slot.
 */
const viewReport = async (slotData) => {
  selectedReport.value = slotData;

  if (selectedReport.value.isReportContact) {
    if (selectedReport.value.reportId) {
      navigateToReport(selectedReport.value.reportId, selectedReport.value.reportType);
    } else {
      await createReport(selectedReport.value.organizationId, selectedReport.value.periodId, null, selectedReport.value.reportType);
    }
  } else {
    dialogVisible.value = true;
  } 
};



const createReport = async (organizationId, periodId, roleId, reportType) => {
  if (organizationId == null || periodId == null) {
    alert.showErrorAlert('Create report', 'An error occurred while creating the report. Please try again.');
    return;
  }

  const reportTypeHandlers = {
    [REPORT_TYPES.Hp]: createAnnualReport,
    [REPORT_TYPES.Fosc]: createFoscReport,
    [REPORT_TYPES.Di]: createDIReport,
    [REPORT_TYPES.Cex]: createCexReport,
    [REPORT_TYPES.Uro]: createUroReport,
  };

  const handler = reportTypeHandlers[reportType];
  
  if (!handler) {
    alert.showErrorAlert('Create report', 'Invalid report type.');
    return;
  }

  const newReportId = await pageHelper.save(
    () => handler(organizationId, periodId, contactId, roleId),
    'Creating report, please wait...'
  );

  if (newReportId) {
    navigateToReport(newReportId, reportType);
  } else {
    alert.showErrorAlert('Create report', 'An error occurred while creating the report. Please try again.');
  }
};

const addUserAndViewReport = async () => {
  dialogVisible.value = false;
  if (selectedRole.value === null) {
    alert.showInfoAlert('Role Required', 'Please select a role to view the report.');
    return;
  }

  if (selectedReport.value === null) {
    alert.showInfoAlert('Report Required', 'Please select a report to view.');
    return;
  }

  if (selectedReport.value.reportId == null) {
    await createReport(selectedReport.value.organizationId, selectedReport.value.periodId, selectedRole.value.attributeId, selectedReport.value.reportType);
  } else {
    // Add the user to the report and navigate to the report.
    await pageHelper.save(() => addContactToReport(selectedReport.value.reportId, selectedReport.value.reportType, contactId, selectedRole.value.attributeId), 'Adding user to report, please wait...');
  }
};

const addContactToReport = async (reportId, reportType, contactId, roleId) => {
  const reportTypeHandlers = {
    [REPORT_TYPES.Hp]: addReportContact,
    [REPORT_TYPES.Fosc]: addFoscReportContact,
    [REPORT_TYPES.Di]: addDiReportContact,
    [REPORT_TYPES.Cex]: addCexReportContact,
    [REPORT_TYPES.Uro]: addUroReportContact,
  };

  const handler = reportTypeHandlers[reportType];
  
  if (!handler) {
    alert.showErrorAlert('Add Report User', 'Invalid report type.');
    return;
  }

  await pageHelper.save(() => handler(reportId, contactId, roleId));

  if (!pageHelper.state.hasError) {
    navigateToReport(reportId, reportType);
  } else {
    alert.showErrorAlert('Add Report User', 'An error occurred while adding the user to the report. Please try again.');
  }
};

const navigateToReport = (reportId, reportType) => {
  const reportRoutes = {
    [REPORT_TYPES.Cex]: 'cexannualreport',
    [REPORT_TYPES.Di]: 'diannualreport',
    [REPORT_TYPES.Fosc]: 'foscannualreport',
    [REPORT_TYPES.Hp]: 'hpannualreport',
    [REPORT_TYPES.Uro]: 'uroannualreport',
  };

  const routeName = reportRoutes[reportType];
  
  if (routeName) {
    router.push({ name: routeName, params: { reportId } });
  } else {
    throw new Error(`Invalid report type: ${reportType}`);
  }
};

// ===============================
// WATCHERS
// ===============================


// ===============================
// LIFECYCLE HOOKS
// ===============================

// ===============================
// EXPOSE
// ===============================


</script>

<style scoped>
.button-width {
  width: 100%;
}
</style>